/**
 * セッションコンテキスト Store
 * session_key 検証結果を保持し、リロード時も同一内容を再利用できるよう localStorage に永続化する
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { STORAGE_KEYS } from '../constants/app.constants';
import { LocalStorageService } from '../services/local-storage.service';
import { normalizeUtcDateTimeString, parseUtcDateTime } from './session-time.util';

/** セッション検証後に保持する認証コンテキスト */
export interface SessionContext {
  /** URL から受け取った session_key */
  sessionKey: string;

  /** セッションに紐づく報告者 ID */
  reporterId: string;

  /** 表示用ユーザー名 */
  userName: string;

  /** セッション有効期限 */
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class SessionContextStore {
  private readonly stateSubject: BehaviorSubject<SessionContext | null>;

  /** セッション状態の購読用 Observable */
  readonly state$: Observable<SessionContext | null>;

  constructor(private readonly storage: LocalStorageService) {
    const storedContext = this.loadFromStorage();
    this.stateSubject = new BehaviorSubject<SessionContext | null>(storedContext);
    this.state$ = this.stateSubject.asObservable();
  }

  /** 現在のセッション状態を同期取得する */
  get snapshot(): SessionContext | null {
    return this.stateSubject.value;
  }

  /**
   * セッション状態を保存して配信する
   * - 同一 session_key の場合は最新 API 応答で上書き更新する
   * - 異なる session_key の場合は既存状態を破棄してから新しい状態へ置き換える
   */
  upsertSession(context: SessionContext): void {
    const normalizedContext = this.normalizeContext(context);
    if (!normalizedContext) {
      this.clear();
      return;
    }

    const currentContext = this.snapshot;
    if (currentContext && currentContext.sessionKey !== normalizedContext.sessionKey) {
      this.clear();
    }

    this.stateSubject.next(normalizedContext);
    this.storage.setItem(STORAGE_KEYS.SESSION_CONTEXT, normalizedContext);
  }

  /** セッション状態を破棄する */
  clear(): void {
    this.stateSubject.next(null);
    this.storage.removeItem(STORAGE_KEYS.SESSION_CONTEXT);
  }

  /** 期限切れかどうかを判定する */
  isExpired(context: SessionContext | null = this.snapshot): boolean {
    if (!context) {
      return true;
    }

    const expiresAtTime = parseUtcDateTime(context.expiresAt);
    if (Number.isNaN(expiresAtTime)) {
      return true;
    }

    return expiresAtTime <= Date.now();
  }

  /** session_key が一致する保存済み状態かどうかを判定する */
  isSameSession(sessionKey: string): boolean {
    return this.snapshot?.sessionKey === sessionKey;
  }

  /** localStorage から保存済み状態を復元する */
  private loadFromStorage(): SessionContext | null {
    const storedContext = this.storage.getItem<SessionContext>(STORAGE_KEYS.SESSION_CONTEXT);
    return this.normalizeContext(storedContext);
  }

  /** 保存前後で expiresAt を UTC ISO 形式へ揃える */
  private normalizeContext(context: SessionContext | null): SessionContext | null {
    if (!context) {
      return null;
    }

    const normalizedExpiresAt = normalizeUtcDateTimeString(context.expiresAt);
    if (!normalizedExpiresAt) {
      return null;
    }

    return {
      ...context,
      expiresAt: normalizedExpiresAt,
    };
  }
}
